import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { parsePairingScan } from './parsePairingScan';
import { usePairingCamera } from './usePairingCamera';

type PairingQrScannerProps = {
  open: boolean;
  screenName: string;
  busy?: boolean;
  approveError?: string | null;
  onCode: (code: string) => void;
  onClose: () => void;
};

/**
 * Full-screen in-app QR capture for TV pairing.
 * Closes on Escape / Cancel; stops the camera when dismissed.
 */
export function PairingQrScanner({
  open,
  screenName,
  busy = false,
  approveError = null,
  onCode,
  onClose,
}: PairingQrScannerProps) {
  const { t } = useTranslation(['displays', 'common']);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [invalidScan, setInvalidScan] = useState<string | null>(null);
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    if (!open) {
      setCameraError(null);
      setInvalidScan(null);
      setResetKey(0);
    }
  }, [open]);

  useEffect(() => {
    if (!approveError) return;
    setResetKey((key) => key + 1);
  }, [approveError]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, busy, onClose]);

  const onFrame = useCallback(
    (raw: string) => {
      if (busy) return false;
      const code = parsePairingScan(raw);
      if (!code) {
        setInvalidScan(t('pairing.scanner.invalidCode'));
        return false;
      }
      setInvalidScan(null);
      onCode(code);
      return true;
    },
    [busy, onCode, t],
  );

  const { videoRef } = usePairingCamera({
    active: open,
    resetKey,
    onFrame,
    onError: setCameraError,
  });

  if (!open) return null;

  const statusMessage = busy
    ? t('pairing.scanner.connecting')
    : approveError || cameraError || invalidScan || t('pairing.scanner.pointAtCode');

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="pairing-qr-title"
      className="fixed inset-0 z-[120] flex flex-col"
      style={{ background: '#0a0a0b', color: '#f2f1ee' }}
    >
      <header className="flex shrink-0 items-start justify-between gap-3 px-4 pb-3 pt-[max(1rem,env(safe-area-inset-top))] sm:px-6">
        <div className="min-w-0">
          <p className="m-0 text-[12px] font-bold uppercase tracking-[0.08em] text-white/55">
            {t('pairing.scanner.eyebrow')}
          </p>
          <h2 id="pairing-qr-title" className="m-0 mt-1 text-xl font-extrabold tracking-[-0.02em]">
            {t('pairing.scanner.title', { name: screenName })}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="shrink-0 rounded-[10px] border border-white/20 px-3.5 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {t('common:actions.cancel')}
        </button>
      </header>

      <div className="relative mx-4 min-h-0 flex-1 overflow-hidden rounded-[16px] bg-black sm:mx-6">
        <video
          ref={videoRef}
          muted
          playsInline
          className="absolute inset-0 h-full w-full object-cover"
          aria-label={t('pairing.scanner.cameraAria')}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-[18%] rounded-[12px] border-2 border-white/80"
          style={{ boxShadow: '0 0 0 9999px color-mix(in srgb, #0a0a0b 45%, transparent)' }}
        />
      </div>

      <footer className="shrink-0 px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-4 sm:px-6">
        <p
          className="m-0 text-center text-sm font-medium leading-snug"
          style={{
            color: approveError || cameraError || invalidScan ? 'var(--gs-bad)' : 'rgba(242,241,238,0.75)',
          }}
          role={approveError || cameraError || invalidScan ? 'alert' : undefined}
        >
          {statusMessage}
        </p>
      </footer>
    </div>,
    document.body,
  );
}
